import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "./utils/apiCall";
import { API_URLS } from "./apis/urls";

interface IntentState {
  data: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "list" | "create" | "delete" | "list_questions" | "create_question" | "delete_question" | "list_answers" | "create_answer" | "bulk_upload" | null;
  faqQuestionData: any;
  createFaqQuestionData: any;
  faqAnswerData: any;
  createFaqAnswerData: any;
  deleteIntentData: any;
  deleteFaqQuestionData: any;
  bulkUploadData: any;
}

const intentInitialState: IntentState = {
  data: null,
  loading: "idle",
  error: null,
  action: null,
  faqQuestionData: null,
  createFaqQuestionData: null,
  faqAnswerData: null,
  createFaqAnswerData: null,
  deleteIntentData: null,
  deleteFaqQuestionData: null,
  bulkUploadData: null,
};

export const createIntent: any = createAsyncThunk(
  "intent/createIntent",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.INTENT.CREATE_INTENT,
        {
          name: payload.name,
          description: payload.description,
          active: payload.active
        }
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getAllIntents: any = createAsyncThunk(
  "intent/getAllIntents",
  async (payload: any = {}, thunkAPI) => {
    try {
      const requestBody: any = {
        page: payload.page || 1,
        limit: payload.limit || 10,
      };

      // Add query parameter if provided (for chatbot search)
      if (payload.query) {
        requestBody.query = payload.query;
      }

      const response = await Api.post(
        API_URLS.INTENT.GET_ALL_INTENTS,
        requestBody
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createFaqQuestion: any = createAsyncThunk(
  "intent/createFaqQuestion",
  async (payload: any, thunkAPI) => {
    try {
      const requestBody = {
        id: payload.id || "",
        intentId: payload.intent_id || "",
        question: payload.question,
        answer: payload.answer,
        language: payload.language || "en"
      };

      const response = await Api.post(
        API_URLS.CHATBOT.CREATE_FAQ_QUESTION,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getAllFaqQuestions: any = createAsyncThunk(
  "intent/getAllFaqQuestions",
  async (payload: any = {}, thunkAPI) => {
    try {
      const requestBody: any = {
        page: payload.page || 1,
        limit: payload.limit || 10,
      };

      // Add search query if provided
      if (payload.search) {
        requestBody.query = payload.search;
      }

      const response = await Api.post(
        API_URLS.CHATBOT.GET_ALL_FAQ_QUESTIONS,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createFaqAnswer: any = createAsyncThunk(
  "intent/createFaqAnswer",
  async (payload: any, thunkAPI) => {
    try {
      const requestBody = {
        intentId: payload.intent_id || "",
        answer: payload.answer,
        language: payload.language || "en"
      };

      const response = await Api.post(
        API_URLS.CHATBOT.CREATE_FAQ_ANSWER,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getAllFaqAnswers: any = createAsyncThunk(
  "intent/getAllFaqAnswers",
  async (payload: any = {}, thunkAPI) => {
    try {
      const requestBody = {
        page: payload.page || 1,
        limit: payload.limit || 10,
      };
      const response = await Api.post(
        API_URLS.CHATBOT.GET_ALL_FAQ_ANSWERS,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteIntent: any = createAsyncThunk(
  "intent/deleteIntent",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.CHATBOT.DELETE_INTENT,
        { id: payload.id }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteFaqQuestion: any = createAsyncThunk(
  "intent/deleteFaqQuestion",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.CHATBOT.DELETE_FAQ_QUESTION,
        { id: payload.id }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const bulkUploadFaqQuestions: any = createAsyncThunk(
  "intent/bulkUploadFaqQuestions",
  async (payload: { filestring: string; mime_type: string; filename: string }, thunkAPI) => {
    try {
      const requestBody = {
        data: {
          filestring: payload.filestring,
          mime_type: payload.mime_type,
          filename: payload.filename,
        },
      };
      const response = await Api.post(
        API_URLS.uploadFile,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const intentSlice = createSlice({
  name: "intent",
  initialState: intentInitialState,
  reducers: {
    resetAll: (state: any) => {
      state.loading = "idle";
      state.error = null;
      state.data = null;
      state.action = null;
      state.faqQuestionData = null;
      state.createFaqQuestionData = null;
      state.faqAnswerData = null;
      state.createFaqAnswerData = null;
      state.deleteIntentData = null;
      state.deleteFaqQuestionData = null;
      state.bulkUploadData = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllIntents.pending, (state) => {
      state.loading = "pending";
      state.data = null;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getAllIntents.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.data = action.payload;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getAllIntents.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.data = null;
      state.action = "list";
    });

    builder.addCase(createIntent.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "create";
    });
    builder.addCase(createIntent.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.error = null;
      state.action = "create";
    });
    builder.addCase(createIntent.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "create";
    });

    builder.addCase(getAllFaqQuestions.pending, (state) => {
      state.loading = "pending";
      state.faqQuestionData = null;
      state.error = null;
      state.action = "list_questions";
    });
    builder.addCase(getAllFaqQuestions.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.faqQuestionData = action.payload;
      state.error = null;
      state.action = "list_questions";
    });
    builder.addCase(getAllFaqQuestions.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.faqQuestionData = null;
      state.action = "list_questions";
    });

    builder.addCase(createFaqQuestion.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "create_question";
    });
    builder.addCase(createFaqQuestion.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.createFaqQuestionData = action.payload;
      state.error = null;
      state.action = "create_question";
    });
    builder.addCase(createFaqQuestion.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "create_question";
    });

    builder.addCase(getAllFaqAnswers.pending, (state) => {
      state.loading = "pending";
      state.faqAnswerData = null;
      state.error = null;
      state.action = "list_answers";
    });
    builder.addCase(getAllFaqAnswers.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.faqAnswerData = action.payload;
      state.error = null;
      state.action = "list_answers";
    });
    builder.addCase(getAllFaqAnswers.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.faqAnswerData = null;
      state.action = "list_answers";
    });

    builder.addCase(createFaqAnswer.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "create_answer";
    });
    builder.addCase(createFaqAnswer.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.createFaqAnswerData = action.payload;
      state.error = null;
      state.action = "create_answer";
    });
    builder.addCase(createFaqAnswer.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "create_answer";
    });

    builder.addCase(deleteIntent.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "delete";
    });
    builder.addCase(deleteIntent.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.deleteIntentData = action.payload;
      state.error = null;
      state.action = "delete";
    });
    builder.addCase(deleteIntent.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "delete";
    });

    builder.addCase(deleteFaqQuestion.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "delete_question";
    });
    builder.addCase(deleteFaqQuestion.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.deleteFaqQuestionData = action.payload;
      state.error = null;
      state.action = "delete_question";
    });
    builder.addCase(deleteFaqQuestion.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "delete_question";
    });

    builder.addCase(bulkUploadFaqQuestions.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "bulk_upload";
    });
    builder.addCase(bulkUploadFaqQuestions.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.bulkUploadData = action.payload;
      state.error = null;
      state.action = "bulk_upload";
    });
    builder.addCase(bulkUploadFaqQuestions.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "bulk_upload";
    });
  },
});

export const { resetAll } = intentSlice.actions;
export default intentSlice.reducer;